import React from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'

import { RefetchQueriesFunction } from '@apollo/react-common'
import { bemJoin } from 'bem-join'
import { Stack, Cluster, Button } from 'component-library-next'

import { FormattedError } from '@app/common/Components/FormattedError'
import {
  AlertType,
  useNotifications
} from '@app/common/services/Notifications/NotificationsProvider'
import { Features } from '@app/Feature'
import { isFeatureEnabled } from '@app/Feature/Store/selectors'
import { DeliveryMethod } from '@app/graphql/types'
import { IntlMessageKeys, useIntl } from '@app/intl/Intl'
import { VisuallyHidden } from '@app/lib/display/VisuallyHidden/VisuallyHidden'
import { ChoiceField } from '@app/lib/fields/ChoiceField/ChoiceField'
import { Radio } from '@app/lib/inputs/Radio/Radio'
import { useCustomerId } from '@app/modules/Messages/hooks/useCustomerId'
import { useCustomerAsLawyerQuery } from '@app/queries/CustomerAsLawyer/CustomerAsLawyer.queries'
import { Message } from '@app/queries/CustomerAsLawyer/models'
import { useOpenPowerOfAttorneyMutation } from '@app/queries/PowerOfAttorney/OpenPowerOfAttorney.queries'

export type PowerOfAttorneyRequestFormProps = {
  letterRequest: Message['letterRequest']
}

export const PowerOfAttorneyRequestForm = ({ letterRequest }: PowerOfAttorneyRequestFormProps) => {
  const methods = useForm()
  const { pushNotification } = useNotifications()
  const { formatMessage } = useIntl()

  const digitalPowerOfAttorneyEnabled = useSelector(
    isFeatureEnabled(Features.POWER_OF_ATTORNEY_DIGITAL)
  )
  const paperPowerOfAttorneyEnabled = useSelector(
    isFeatureEnabled(Features.POWER_OF_ATTORNEY_PAPER)
  )

  const b = bemJoin('power-of-attorney-request-form')

  const customerId = useCustomerId()

  const { loading: letterRequestInfoLoading, refetch: refetchletterRequestInfo } =
    useCustomerAsLawyerQuery({ variables: { customerId }, fetchPolicy: 'cache-first' })

  const [openPowerOfAttorneyMutation, { loading: openingPowerOfAttorney }] =
    useOpenPowerOfAttorneyMutation({
      onError: (error) => {
        pushNotification({
          type: AlertType.error,
          message: <FormattedError error={error} />
        })
      }
    })

  const openPowerOfAttorney = async (deliveryMethod: DeliveryMethod) => {
    if (openingPowerOfAttorney || letterRequestInfoLoading) {
      return
    }

    await openPowerOfAttorneyMutation({
      refetchQueries: refetchletterRequestInfo as unknown as RefetchQueriesFunction,
      variables: {
        powerOfAttorneyBaseInput: {
          letterRequestId: letterRequest!.id,
          deliveryMethod
        }
      }
    })
  }

  return (
    <FormProvider {...methods}>
      <form
        aria-label='Vollmacht nachfragen'
        onSubmit={methods.handleSubmit((data) => openPowerOfAttorney(data.deliveryMethod))}
      >
        <Stack>
          <ChoiceField<DeliveryMethod>
            name={'deliveryMethod'}
            label={(labelProps) => (
              <VisuallyHidden>
                <label {...labelProps}>delivery-method</label>
              </VisuallyHidden>
            )}
            rules={{
              required: {
                value: true,
                message: formatMessage({ id: 'PowerOfAttorney.Request.Dialogue.Required' })
              }
            }}
            defaultValue={!!digitalPowerOfAttorneyEnabled ? DeliveryMethod.Digital : undefined}
            render={({ register }) => (
              <Cluster role='radiogroup' className={b('delivery-method')}>
                <div>
                  {formatMessage({
                    id: `PowerOfAttorney.Request.Dialogue.RadioTitle` as IntlMessageKeys
                  })}
                </div>
                {!!digitalPowerOfAttorneyEnabled && (
                  <Radio {...register({ value: DeliveryMethod.Digital })}>
                    {formatMessage({
                      id: `PowerOfAttorney.DeliveryMethod.Digital` as IntlMessageKeys
                    })}
                  </Radio>
                )}
                {!!paperPowerOfAttorneyEnabled && (
                  <Radio {...register({ value: DeliveryMethod.Paper })}>
                    {formatMessage({
                      id: `PowerOfAttorney.DeliveryMethod.Paper` as IntlMessageKeys
                    })}
                  </Radio>
                )}
              </Cluster>
            )}
          />
          <Button
            color={Button.colors.secondary}
            disabled={openingPowerOfAttorney || letterRequestInfoLoading}
            loading={openingPowerOfAttorney || letterRequestInfoLoading}
            size={Button.sizes.medium}
            type={Button.types.submit}
            testId='poa__delivery__method__submit'
          >
            {formatMessage({ id: 'PowerOfAttorney.Request.New' })}
          </Button>
        </Stack>
      </form>
    </FormProvider>
  )
}
